// tabs/mentas.ts — client renderer for the PC sheet's Mentas tab.
//
// Menta rows and their effects derive from the browser-held creature array.
// The existing table renderer retains the common spell/menta pickers.
//
// Rows use mentas_tr/mentas_tr_<level>/[mentas_tr_concentration] + the section divs mentas_div/
// mentas_div_<level> so the console pills' showByClass/hideByClass globals filter them. (The legacy
// menta_tr emitted spells_known_tr classes — a copy-paste mismatch with its own filter; fixed here.)

import type { CachedCaster, CasterItemNode } from '../types';
import { generateAbilityButton } from '../ability-button';
import { sheetMentasPayload } from '../creature-selectors';
import { displayRefusal } from '../k5-runtime';
import { getSpellTimeShortLabel } from '../../shared/spell-casting-time';
import { rangeIcon, displayAoeCompact, displayDuration } from '../../shared/spell-display';
import { decodeHtmlEntitiesForPlainText } from '../../shared/decode-html-entities';
import { userChoicePickerLabel, statusChoiceSelect } from './ability-row';
import { effectButtonUsesAbility, useRowAbilitySnippet } from './ability-effect-button';

interface Choice { id: string; name: string }
interface MentaEffect { dice: unknown; die: unknown; mod: unknown; roll_type_id: string; damage_type: string; has_damage_type_icon: boolean; is_healing: boolean }
interface MentaUpcast { kind: string; min: unknown; max: unknown; value: unknown }
interface MentaObj {
    nest: CasterItemNode;
    id: string; name: string; level: number; concentration: boolean;
    effect: MentaEffect | null; summon_choices: Choice[] | null; upcast: MentaUpcast | null;
    /** status_effect_applied_is_caster_choice (2026-10-07): the pick-one status options, server-named. */
    status_choices?: Choice[] | null;
    /** ability_choice BRANCH list (2026-10-07, Psion v12 mentas): server-resolved like the castable spell table's. */
    ability_choices?: Choice[] | null;
}

/** An ability_choice PARENT row: the cast fires the picked branch (handleAbilityChoiceChange swaps the child's button in). */
function isChoiceParent(o: MentaObj): boolean {
    return !!o.ability_choices && o.ability_choices.length > 0;
}
/** The caster-cache identity for the browser branch renderer (castable-spells.ts casterRequestId twin). */
function casterRequestId(caster: CachedCaster, fallback: string): string {
    const ec = (caster as { encounter_creatures?: unknown } | null)?.encounter_creatures as Record<string, unknown> | undefined;
    const raw = ec && !Array.isArray(ec) ? ec.encounter_creature_id : undefined;
    const id = String((Array.isArray(raw) ? raw[0] : raw) ?? '');
    return id || fallback;
}
/** The branch picker (`input_<parentId>_ability_choice_branch`) — the shared handleAbilityChoiceChange (template.js) derives the selected child in the browser and and paints the CHILD's button / name / effect / columns into this row's data-ac-slot divs. */
function branchSelect(o: MentaObj, canEdit: boolean): string {
    let opts = '<option value="">-- SELECT OPTION --</option>';
    for (const c of o.ability_choices ?? []) opts += `<option value="${escapeHtml(c.id)}">${escapeHtml(c.name)}</option>`;
    return `<div class="mb-2"><select class="ts-select form-select form-select-sm" id="input_${escapeHtml(o.id)}_ability_choice_branch"`
        // + intern the picked branch's definition: the browser ability lane resolves the cast item from the object cache, and a branch is
        //   held by no caster bucket — without it the cast took the PHP path (`definition-not-held`). R-198, 2026-10-07.
        + ` onchange="handleAbilityChoiceChange(this); if (this.value && window.fetchAndCacheObject) window.fetchAndCacheObject(this.value).catch(function () {});"`
        + `${canEdit ? '' : ' disabled'}>${opts}</select></div>`;
}
interface MentasPayload { mentas: MentaObj[]; max_menta_level_castable: number; can_edit: boolean; error?: string }

function spellTimeDisplay(nest: CasterItemNode): string {
    return getSpellTimeShortLabel(String(ftLayerValue(nest, 'spell_casting_time') ?? ''));
}
function runeInline(nest: CasterItemNode): string {
    let json = 'null';
    try { json = JSON.stringify({ items: nest?.items ?? {}, item_layer_types: nest?.item_layer_types ?? {}, catalog: nest?.catalog ?? {} }); } catch { json = 'null'; }
    json = json.replace(/<\//g, '<\\/');
    return `<div class="ft-rune-mount ft-rune-inline" data-rune-size="22"><script type="application/json" class="ft-rune-blob">${json}</script></div>`;
}
function ordinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
    return n + (s[(v - 20) % 10] ?? s[v] ?? 'th');
}

function upcastCell(o: MentaObj, canEdit: boolean): string {
    if (!o.upcast) return '';
    const u = o.upcast;
    return `<input id="input_${escapeHtml(o.id)}_spell_upcastable" class="form-control form-control-sm" type="number" min="${escapeHtml(String(u.min ?? ''))}" max="${escapeHtml(String(u.max ?? ''))}" value="${escapeHtml(String(u.value ?? ''))}"${canEdit ? '' : ' disabled'}>`;
}
function rangeCell(o: MentaObj): string {
    return rangeIcon(o.nest);
}
function aoeCell(o: MentaObj): string {
    return displayAoeCompact(o.nest) || '—';
}
function durationCell(o: MentaObj): string {
    let out = displayDuration(o.nest, true);
    if (o.concentration) out += `<br />${iconMap('concentration')}`;
    return out;
}
function rollCell(o: MentaObj): string {
    const contestee = ftLayerValue(o.nest, 'skill_roll_contestee');
    const rt = ftLayerValue(o.nest, 'roll_type');
    const id = contestee ? String(contestee) : (rt ? String(rt) : '');
    return id ? `<button class='btn btn-warning btn-sm text-dark border border-dark'>${iconMap(id)}</button>` : '';
}
function summonSelect(o: MentaObj, canEdit: boolean): string {
    if (!o.summon_choices || o.summon_choices.length === 0) return '';
    const selId = `input_${o.id}_user_choice_1_summon`;
    // The EMPTY option's text is the native fallback placeholder (Tom Select uses the attribute).
    const label = userChoicePickerLabel(selId);
    let opts = `<option value="">${escapeHtml(label)}</option>`;
    for (const c of o.summon_choices) opts += `<option value="${escapeHtml(c.id)}">${escapeHtml(c.name)}</option>`;
    // Ungated + compact for the same reasons as ability-row.ts choiceSelect: openItemInfoValue is a
    // READ, and ts-auto-width must sit on the wrapper for the descendant CSS to match.
    // No `name`: nothing reads it and the sibling pickers set none — an ENABLED named control would
    // otherwise be a successful form control if this table ever sat inside a <form>. (Codex 2026-08-29.)
    return `<div class="ts-auto-width mt-1"><select class="ts-select ts-auto-width" id="${escapeHtml(selId)}"`
        + ` placeholder="${escapeHtml(label)}" title="${escapeHtml('Choose ' + label)}"`
        + ` onchange="openItemInfoValue('${escapeHtml(selId)}');">${opts}</select></div>`;
}
function effectCell(o: MentaObj, canEdit: boolean): string {
    // Choice parent: the picker sits ABOVE the effect slot (handleAbilityChoiceChange replaces the slot's innerHTML wholesale).
    if (isChoiceParent(o)) return branchSelect(o, canEdit) + '<div data-ac-slot="effect"></div>';
    let html = summonSelect(o, canEdit) + statusChoiceSelect(o.nest, o.id, o.status_choices ?? null);
    const e = o.effect;
    if (!e) return html;
    const sampleRoll = `rollDice('${escapeHtml(e.roll_type_id)}','${escapeHtml(e.damage_type)}','${escapeHtml(o.id)}','','${escapeHtml(String(e.dice))}','${escapeHtml(String(e.die))}','${escapeHtml(String(e.mod))}','0');`;
    // Heal / Self-range mentas USE the menta from this button (owner 2026-09-17) — see effectButtonUsesAbility().
    const uses = effectButtonUsesAbility(o.nest, e.roll_type_id, e.is_healing);
    const onclick = uses ? useRowAbilitySnippet(o.id, sampleRoll) : sampleRoll;
    let btn = `<button type="button" class="btn btn-sm btn-danger border border-dark"${uses ? ` title="Use ${escapeHtml(o.name)}"` : ''}${canEdit ? ` onclick="${onclick}"` : ' disabled'}>`;
    btn += `${escapeHtml(String(e.dice))}d${escapeHtml(String(e.die))}`;
    if (Number(e.mod) > 0) btn += ` + ${escapeHtml(String(e.mod))}`;
    if (e.has_damage_type_icon) btn += ' ' + iconMap(e.damage_type);
    if (e.is_healing) btn += ' ' + iconMap('healing');
    btn += '</button>';
    return html + btn;
}

function mentaRow(o: MentaObj, caster: CachedCaster, canEdit: boolean, casterId: string): string {
    const cls = `mentas_tr mentas_tr_${o.level}${o.concentration ? ' mentas_tr_concentration' : ''}`;
    if (isChoiceParent(o)) {
        // The branch carries its own cost / range / duration: every child-derived cell is a data-ac-slot the pick fills.
        const attrs = ` data-ft-ability-choice-parent="${escapeHtml(o.id)}" data-ft-caster-item-id="${escapeHtml(casterRequestId(caster, casterId))}"`
            + ` data-ft-ac-spell-type="" data-ft-ac-source="" data-ft-ac-consume-nested="0" data-ft-ac-extend-spell-columns="1"`;
        return `<tr class="${cls} fate-tide-ability-choice-row" data-spell-name="${escapeHtml(o.name.toLowerCase())}"${attrs}>
        <td class="text-center text-nowrap">
            <div data-ac-slot="button"></div>
            <div data-ac-slot="cast_time"></div>
        </td>
        <td>${upcastCell(o, canEdit)}</td>
        <td><div data-ac-slot="name" class="mb-1"></div><div class="d-flex align-items-center">${runeInline(o.nest)}<span role="button" class="link-primary small text-secondary" onclick="openItemInfo('${escapeHtml(o.id)}');">${escapeHtml(o.name)}</span></div></td>
        <td class="text-center"><div data-ac-slot="range"></div></td>
        <td class="text-center text-nowrap"><div data-ac-slot="aoe"></div></td>
        <td class="text-center text-nowrap"><div data-ac-slot="duration"></div></td>
        <td class="text-center"><div data-ac-slot="hit_dc"></div></td>
        <td>${effectCell(o, canEdit)}</td>
    </tr>`;
    }
    return `<tr class="${cls}" data-spell-name="${escapeHtml(o.name.toLowerCase())}">
        <td class="text-center text-nowrap">
            <div>${generateAbilityButton(o.nest, caster, { buttonDisabled: !canEdit })}</div>
            <div>${escapeHtml(spellTimeDisplay(o.nest))}</div>
        </td>
        <td>${upcastCell(o, canEdit)}</td>
        <td><div class="d-flex align-items-center">${runeInline(o.nest)}<span role="button" class="link-primary" onclick="openItemInfo('${escapeHtml(o.id)}');">${escapeHtml(o.name)}</span></div></td>
        <td class="text-center">${rangeCell(o)}</td>
        <td class="text-center text-nowrap">${aoeCell(o)}</td>
        <td class="text-center text-nowrap">${durationCell(o)}</td>
        <td class="text-center">${rollCell(o)}</td>
        <td>${effectCell(o, canEdit)}</td>
    </tr>`;
}

function levelTable(level: number, rows: string): string {
    return `<div class="mentas_div mentas_div_${level}">
        <div><h5 class="mt-1 mb-0 pt-2 pb-2 pl-2 bg-dark text-light bg-gradient rounded-top text-left">${ordinal(level)} Menta Levels</h5></div>
        <div class="table-responsive-xxl pl-1 pr-1 pb-2 pt-0 mb-3"><table class="table table-hover table-sm">
            <thead><tr>
                <th class="text-center" style="width:55px;">${iconMap('menta')}</th>
                <th class="text-center" style="width:65px;">${iconMap('spell_upcastable')}</th>
                <th class="col-3">Name</th>
                <th class="text-center" style="width:55px;">${iconMap('range')}</th>
                <th class="text-center" style="width:83px;">AOE</th>
                <th class="text-center" style="width:83px;">${iconMap('duration')}</th>
                <th class="text-center" style="width:83px;">${iconMap('roll_type')}</th>
                <th class="text-left">Effect</th>
            </tr></thead>
            <tbody>${rows}</tbody>
        </table></div>
    </div>`;
}

function navPills(maxLevel: number): string {
    if (maxLevel < 0) return '';
    const pill = (label: string, onclick: string, active = false): string =>
        `<li class="nav-item p-1" role="presentation"><button class="nav-link border${active ? ' active' : ''}" type="button" onclick="${onclick}">${label}</button></li>`;
    let pills = pill('All', "showByClass('mentas_div'); showByClass('mentas_tr');", true);
    for (let x = 0; x <= 9; x++) {
        if (maxLevel >= x) pills += pill(String(x), `hideByClass('mentas_div'); showByClass('mentas_div_${x}'); hideByClass('mentas_tr'); showByClass('mentas_tr_${x}');`);
    }
    pills += pill(iconMap('concentration'), "showByClass('mentas_div'); hideByClass('mentas_tr'); showByClass('mentas_tr_concentration');");
    return `<div class="mt-1 mb-2"><ul class="nav nav-pills" role="tablist">${pills}</ul></div>`;
}

export async function renderMentas(caster: CachedCaster, casterId: string, container: HTMLElement): Promise<void> {
    let payload: MentasPayload;
    try { payload = sheetMentasPayload(caster) as unknown as MentasPayload; }
    catch (error) { container.innerHTML = displayRefusal(error); return; }

    // item_name is persisted HTML-ENCODED in the DB ("Death&#039;s Whisper") and the API hands it over
    // verbatim; escapeHtml() would re-encode the entity's & and print a literal "&#039;". Decode ONCE
    // here at ingest so the row name, the summon-choice options and the lowercased data-spell-name the
    // search filter matches against are all true plain text. Mirrors prepare-spells.ts / inventory.ts.
    (payload.mentas ?? []).forEach((m) => {
        m.name = decodeHtmlEntitiesForPlainText(m.name);
        (m.summon_choices ?? []).forEach((c) => { c.name = decodeHtmlEntitiesForPlainText(c.name); });
        (m.status_choices ?? []).forEach((c) => { c.name = decodeHtmlEntitiesForPlainText(c.name); });
        (m.ability_choices ?? []).forEach((c) => { c.name = decodeHtmlEntitiesForPlainText(c.name); });
    });

    let tables = '';
    for (let x = 1; x <= 9; x++) {
        if (payload.max_menta_level_castable < x) continue;
        const rows = payload.mentas.filter((m) => m.level === x).map((m) => mentaRow(m, caster, payload.can_edit, casterId)).join('');
        tables += levelTable(x, rows);
    }

    container.innerHTML = `<div class="ft-pc-mentas">${navPills(payload.max_menta_level_castable)}${tables}</div>`;
    window.ftMountRuneIcons?.(container);
    if (typeof window.initializeTomSelect === 'function') { try { window.initializeTomSelect(); } catch { /* noop */ } }
}
